import type { Category, MilestoneDefinition } from './types'
import { formatCzkShort, formatNumber } from '@/lib/format'
import type { CurrentGoal } from './evolution'

export function formatCurrentGoal(
  cg: CurrentGoal,
  category: Category,
  locale: 'cs' | 'en' = 'cs',
): string {
  const cur = cg.current
  const goal = cg.goal
  switch (category) {
    case 'growth':
      return `${formatNumber(cur, locale)} / ${formatNumber(goal, locale)}`
    case 'revenue':
      return `${formatCzkShort(cur, locale)} / ${formatCzkShort(goal, locale)}`
    case 'reliability':
      return `${formatNumber(cur, locale)} / ${formatNumber(goal, locale)} d`
    case 'speed':
      return `${formatNumber(cur, locale)} / ${formatNumber(goal, locale)}`
  }
}

export function shortAchievementLabel(
  def: MilestoneDefinition,
  locale: 'cs' | 'en' = 'cs',
): string {
  const t = def.threshold
  const cs = locale === 'cs'
  switch (def.metric) {
    case 'clients.active':
      return cs ? `${formatNumber(t, locale)} klientů` : `${formatNumber(t, locale)} clients`
    case 'clients.newThisMonth':
      return cs ? `${t} klientů / měs` : `${t} clients / mo`
    case 'revenue.mrrCzk':
      return cs
        ? `Příjem ze Stripe ${formatCzkShort(t, locale)}`
        : `Stripe income ${formatCzkShort(t, locale)}`
    case 'incidents.open':
      return cs ? `${t} otevřených ↓` : `${t} open ↓`
    case 'incidents.closedToday':
      return cs ? `${t} vyřešeno / den` : `${t} closed / day`
    case 'support.mttrMin':
      return cs ? `Řešení do ${t} min` : `Resolved within ${t} min`
    case 'support.csat':
      return cs ? `Spokojenost ${t.toFixed(1)}` : `Satisfaction ${t.toFixed(1)}`
    case 'support.nps':
      return cs ? `Doporučení ${t}` : `Recommendation score ${t}`
    case 'churn.ratePct':
      return cs ? `Odchod klientů ≤ ${t} %` : `Churn ≤ ${t} %`
    case 'reliability.daysWithoutP1':
      return cs ? `${t} d bez výpadku` : `${t} d no outage`
    case 'incidents.closedTotal':
      return cs
        ? `${formatNumber(t, locale)} chyb vyřešeno`
        : `${formatNumber(t, locale)} issues resolved`
  }
}

export function describeMilestone(
  def: MilestoneDefinition,
  locale: 'cs' | 'en' = 'cs',
): { title: string; flavor: string; goalLabel: string } {
  const t = def.threshold
  const cs = locale === 'cs'

  switch (def.metric) {
    case 'clients.active':
      return {
        title: cs
          ? `${formatNumber(t, locale)} aktivních klientů`
          : `${formatNumber(t, locale)} active clients`,
        flavor: cs ? 'Komunita roste.' : 'The community is growing.',
        goalLabel: `${formatNumber(t, locale)}`,
      }
    case 'revenue.mrrCzk':
      return {
        title: cs
          ? `Příjem ze Stripe ${formatCzkShort(t, locale)}`
          : `Stripe income ${formatCzkShort(t, locale)}`,
        flavor: cs ? 'Platby od zákazníků chodí.' : 'Customer payments are coming in.',
        goalLabel: formatCzkShort(t, locale),
      }
    case 'incidents.open':
      return {
        title: cs ? `${t} otevřených incidentů nebo méně` : `${t} or fewer open incidents`,
        flavor: cs ? 'Klid v provozu.' : 'Smooth sailing.',
        goalLabel: `≤ ${t}`,
      }
    case 'incidents.closedToday':
      return {
        title: cs ? `${t} incidentů vyřešeno za den` : `${t} incidents closed in a day`,
        flavor: cs ? 'Support drtí.' : 'Support on fire.',
        goalLabel: `${t}`,
      }
    case 'support.mttrMin':
      return {
        title: cs ? `Řešení do ${t} minut` : `Resolved within ${t} min`,
        flavor: cs ? 'Reagujeme rychle.' : 'Lightning response.',
        goalLabel: `≤ ${t} min`,
      }
    case 'support.csat':
      return {
        title: cs ? `Spokojenost ${t.toFixed(1)}` : `Satisfaction ${t.toFixed(1)}`,
        flavor: cs ? 'Zákazníci jsou nadšení.' : 'Customers love it.',
        goalLabel: t.toFixed(1),
      }
    case 'support.nps':
      return {
        title: cs ? `Doporučení ${t}` : `Recommendation score ${t}`,
        flavor: cs ? 'Doporučují nás dál.' : 'They recommend us.',
        goalLabel: `${t}`,
      }
    case 'reliability.daysWithoutP1':
      return {
        title: cs ? `${t} dní bez výpadku` : `${t} days without an outage`,
        flavor: cs ? 'Klid a pohoda.' : 'Steady as it gets.',
        goalLabel: `${t} d`,
      }
    case 'clients.newThisMonth':
      return {
        title: cs ? `${t} nových klientů za měsíc` : `${t} new clients this month`,
        flavor: cs ? 'Obchod letí.' : 'Sales engine running hot.',
        goalLabel: `${t}`,
      }
    case 'churn.ratePct':
      return {
        title: cs ? `Odchod klientů pod ${t} %` : `Churn under ${t}%`,
        flavor: cs ? 'Klienti zůstávají.' : 'Customers stick around.',
        goalLabel: `≤ ${t} %`,
      }
    case 'incidents.closedTotal':
      return {
        title: cs
          ? `${formatNumber(t, locale)} vyřešených chyb`
          : `${formatNumber(t, locale)} issues resolved`,
        flavor: cs ? 'Support letí.' : 'Support on a roll.',
        goalLabel: `${formatNumber(t, locale)}`,
      }
  }
}
